import { ApiClient } from '@api/api-configuration';
import { captureError } from '@app/lib/Sentry';
import { ApiInterceptorContext } from '@context/api/ApiInterceptorContext';
import useNotifications from '@context/notifications/useNotificationContext';
import { OIDC_TOKEN_KEY } from '@feat-auth/auth.config';
import { clearGlobalSearchPrefs } from '@app/features/globalSearch/prefs';
import useSmartErrorHandling from '@hooks/useSmartErrorHandling';
import { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import React, { ReactNode, useCallback, useEffect } from 'react';
import { useAuth } from 'react-oidc-context';

interface ApiInterceptorProviderProps {
  children: ReactNode;
}

// Property to mark requests that should skip global error handling
export const SKIP_GLOBAL_ERROR_HANDLER = '__skipGlobalErrorHandler__' as const;

// Global state to suppress next error notification (for manual error handling)
let suppressNextErrorNotification = false;

// A burst of parallel requests can each receive a 401 at once. Without a guard
// every one of them would fire its own signinRedirect. This ensures only the
// first 401 kicks off the redirect; it is reset if the redirect fails to start.
let authRedirectInFlight = false;

// Function to suppress the next error notification (used by manual error handlers)
export const suppressNextGlobalErrorNotification = () => {
  suppressNextErrorNotification = true;
};

// Function to check if next error should be suppressed
export const shouldSuppressNextGlobalError = () => {
  const shouldSuppress = suppressNextErrorNotification;
  if (shouldSuppress) {
    suppressNextErrorNotification = false; // Reset after checking
  }
  return shouldSuppress;
};

// Function to add skip global error handler to request config
export const skipGlobalErrorHandler = <T extends AxiosRequestConfig>(config: T): T => {
  return {
    ...config,
    [SKIP_GLOBAL_ERROR_HANDLER]: true,
  } as T;
};

export const ApiInterceptorProvider: React.FC<ApiInterceptorProviderProps> = ({ children }) => {
  const auth = useAuth();
  const { handleError } = useSmartErrorHandling();
  const { openNotification } = useNotifications();

  const handleApiError = useCallback(
    (error: AxiosError) => {
      // Check if we should suppress this error notification used when wrapper function pattern
      const shouldSuppress = shouldSuppressNextGlobalError();
      if (shouldSuppress) {
        return Promise.reject(error);
      }

      const status = error?.response?.status;
      const config = error.config as AxiosRequestConfig & { [SKIP_GLOBAL_ERROR_HANDLER]?: boolean };

      if (status === 401) {
        localStorage.removeItem('jwt_token');
        localStorage.removeItem(OIDC_TOKEN_KEY);
        clearGlobalSearchPrefs();

        // Only the first 401 in a burst should drive the redirect.
        if (!authRedirectInFlight) {
          authRedirectInFlight = true;

          // Tell the user why they're being bounced to login. The current
          // route is already preserved by useTrackLocation (redirectAfterLogin),
          // so they land back where they were after re-authenticating.
          openNotification('topRight', {
            title: 'Session expired',
            description: 'Signing you in again…',
            type: 'Warning',
          });

          // signinRedirect navigates away almost immediately, which would tear
          // the page down before the toast paints. Delay the redirect briefly
          // so the message is actually seen. If it rejects before navigating we
          // clear the guard so a later 401 can retry instead of getting stuck.
          setTimeout(() => {
            void auth.signinRedirect().catch((redirectError) => {
              authRedirectInFlight = false;
              captureError(redirectError instanceof Error ? redirectError : new Error(String(redirectError)), {
                context: 'ApiInterceptorProvider 401 — signinRedirect failed to start',
              });
            });
          }, 1200);
        }

        return Promise.reject(error);
      }

      // Skip global error handling if marked
      if (config?.[SKIP_GLOBAL_ERROR_HANDLER]) {
        return Promise.reject(error);
      }

      // Use smart error handling for consistent notification management
      try {
        handleError(error, { forceShow: true });
      } catch {
        // handleError re-throws the error, which is expected behavior
      }

      return Promise.reject(error);
    },
    [auth, handleError, openNotification]
  );

  useEffect(() => {
    const interceptorIds: number[] = [];

    const attachInterceptors = (instance: { instance: AxiosInstance }) => {
      const interceptorId = instance.instance.interceptors.response.use(
        (response: AxiosResponse) => response,
        handleApiError
      );
      interceptorIds.push(interceptorId);
    };

    // Attach error handling interceptors to all API instances
    Object.entries(ApiClient).forEach(([, apiInstance]) => {
      attachInterceptors(apiInstance);
    });

    // Cleanup function to remove interceptors
    return () => {
      Object.entries(ApiClient).forEach(([, apiInstance], index) => {
        if (interceptorIds[index] !== undefined) {
          apiInstance.instance.interceptors.response.eject(interceptorIds[index]);
        }
      });
    };
  }, [handleApiError]);

  return <ApiInterceptorContext.Provider value={{}}>{children}</ApiInterceptorContext.Provider>;
};
